import React from 'react';
import { motion } from 'framer-motion';
import './About.css';

const About = () => {
  const stats = [
    { number: '500+', label: 'Projects Completed', icon: 'fas fa-project-diagram' },
    { number: '15+', label: 'Years Experience', icon: 'fas fa-calendar-alt' },
    { number: '50+', label: 'Expert Team Members', icon: 'fas fa-users' },
    { number: '99%', label: 'Client Satisfaction', icon: 'fas fa-smile' }
  ];



  const values = [
    {
      icon: 'fas fa-award',
      title: 'Excellence',
      description: 'We strive for excellence in every project, delivering superior quality and innovative solutions.'
    },
    {
      icon: 'fas fa-handshake',
      title: 'Integrity',
      description: 'Built on trust and transparency, we maintain the highest ethical standards in all our dealings.'
    },
    {
      icon: 'fas fa-lightbulb',
      title: 'Innovation',
      description: 'Embracing cutting-edge technology and creative solutions to meet evolving industry needs.'
    },
    {
      icon: 'fas fa-users-cog',
      title: 'Collaboration',
      description: 'Working closely with clients and partners to achieve shared goals and mutual success.'
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-background">
          <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="About Us" />
        </div>
        <div className="hero-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="hero-content"
          >
            <h1>About Efficient Group</h1>
            <p>Leading the future of facilities management with innovation, expertise, and dedication</p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="company-story section">
        <div className="container">
          <div className="story-content">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="story-text"
            >
              <h2 className="section-title">Our Story</h2>
              <p className="section-subtitle">
                Founded with a vision to revolutionize facilities management
              </p>
              <div className="story-description">
                <p>
                  Efficient Group was established with a clear mission: to provide comprehensive 
                  facilities management solutions that exceed expectations. Our journey began with 
                  a small team of dedicated professionals who shared a passion for excellence and 
                  innovation in the industry.
                </p>
                <p>
                  Over the years, we have grown into a leading provider of industrial design, 
                  MEP services, training programs, and maintenance solutions. Our commitment to 
                  quality and customer satisfaction has earned us the trust of clients across 
                  various sectors.
                </p>
                <p>
                  Today, we continue to push boundaries, embrace new technologies, and deliver 
                  solutions that help our clients achieve their operational goals efficiently 
                  and sustainably.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="story-images"
            >
              <div className="image-collage">
                <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Team Meeting" />
                <img src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Equipment" />
                <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Training" />
                <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Kitchen Design" />
                <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Blueprint" />
                <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Office" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section parallax-bg">
        <div className="parallax-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Achievements</h2>
            <p className="section-subtitle">Numbers that speak for our excellence</p>
          </motion.div>
          
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="stat-card"
                whileHover={{ y: -10 }}
              >
                <div className="stat-icon">
                  <i className={stat.icon}></i>
                </div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-subtitle">
              The principles that guide everything we do
            </p>
          </motion.div>
          
          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="value-card"
                whileHover={{ y: -5 }}
              >
                <div className="value-icon">
                  <i className={value.icon}></i>
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* Mission & Vision */}
      <section className="mission-vision section parallax-bg">
        <div className="parallax-overlay"></div>
        <div className="container">
          <div className="mission-vision-content">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mission-card"
            >
              <div className="card-icon">
                <i className="fas fa-bullseye"></i>
              </div>
              <h3>Our Mission</h3>
              <p>
                To provide exceptional facilities management solutions that enhance 
                operational efficiency, ensure safety, and deliver sustainable value 
                to our clients through innovative design, expert training, and 
                reliable maintenance services.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="vision-card"
            >
              <div className="card-icon">
                <i className="fas fa-eye"></i>
              </div>
              <h3>Our Vision</h3>
              <p>
                To be the leading facilities management company recognized for 
                excellence, innovation, and sustainability. We envision a future 
                where our solutions contribute to smarter, more efficient, and 
                environmentally responsible facilities worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;